import { Link } from "react-router-dom";
import { cn } from "@shared/lib/utils";
import { Countdown } from "@/components/renewal/Countdown";
import { CountUp } from "@/components/renewal/fx/CountUp";
import { computeStanding } from "@/lib/renewal/seasonHub";
import { SEASON_CONFIG } from "@/lib/season";

/**
 * §2(A)1 season strip · pokevault-reskin-spec §3.2 신뢰 패널 — 골드 헤어라인
 * 패널에 eyebrow `SEASON 1` + season timer + key/value 3쌍(My tier/Rank/
 * Total IP). key는 골드 눈썹(mono 11px), value는 잉크 tnum. 우하단 스냅샷
 * 캡션 1줄(데이터형). Whole strip is a 1-tap link into /season. Signed out /
 * not loaded → em-dashes (CountUp not fired). home-ui-spec §3: Total IP
 * value only gets a one-shot 900ms CountUp.
 */

/* §3.2 — key 골드 눈썹 오버라이드 (StatPair 라벨의 골드판) */
const KEY_CLASS = "font-mono text-[11px] font-medium uppercase tracking-[0.16em] text-accent";
const VALUE_CLASS = "font-mono text-sm font-medium tabular-nums text-ink";

function GoldPair({ label, value }: { label: string; value: string | null }) {
  return (
    <span className="flex min-w-0 flex-col gap-0.5">
      <span className={KEY_CLASS}>{label}</span>
      <span className={VALUE_CLASS}>{value ?? "—"}</span>
    </span>
  );
}

export function SeasonStrip({ ip }: { ip: number | null }) {
  const standing = ip != null ? computeStanding(ip) : null;
  const today = new Date().toISOString().slice(0, 10);

  return (
    <Link
      to="/season"
      aria-label="Season 1 — my standing"
      className={cn(
        "flex min-h-11 flex-col gap-2 rounded-2xl border border-(--ty-line-gold) bg-(--ty-bg-2) px-4 py-3.5 transition-colors duration-200 hover:border-accent/50",
        "outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent/70",
      )}
    >
      <span className="flex flex-wrap items-center justify-between gap-x-8 gap-y-3">
        <span className="flex items-center gap-4">
          {SEASON_CONFIG.endsAt && (
            <span className="flex items-baseline gap-1.5">
              <span className="text-xs text-ink-mute">Ends in</span>
              <Countdown to={SEASON_CONFIG.endsAt} className={VALUE_CLASS} />
            </span>
          )}
        </span>

        <span className="flex items-center gap-6 sm:gap-8">
          <GoldPair label="My tier" value={standing?.tier.label ?? null} />
          <GoldPair
            label="Rank"
            value={standing ? `#${standing.rank.toLocaleString("en-US")}` : null}
          />
          {/* v2 S3 — 로그인·standing 존재 시에만: 다음 티어까지 필요 IP(tnum) */}
          {standing && (
            <GoldPair
              label="Next tier in"
              value={`${standing.ipToNextTier.toLocaleString("en-US")} IP`}
            />
          )}
          {/* §3 — Total IP만 CountUp(900ms·1회성·tnum·최종값 폭 선점). 미로드 = — */}
          <span className="flex min-w-0 flex-col gap-0.5">
            <span className={KEY_CLASS}>Total IP</span>
            <span className={VALUE_CLASS}>
              {ip != null ? (
                <>
                  <CountUp to={ip} duration={900} />
                  <span className="ml-1 font-normal text-ink-mute">IP</span>
                </>
              ) : (
                "—"
              )}
            </span>
          </span>
        </span>
      </span>

      {/* 다음 티어 진행 바 — 게임 프로그레션 신호. 분모는 목(현재 IP + 잔여 IP),
       * §6.6.2 금지 항목(환산가 등) 미표시 — "Next tier in" 수치의 시각화만. */}
      {standing && ip != null && (
        <span aria-hidden className="block h-1 w-full overflow-hidden rounded-full bg-(--ty-bg-3)">
          <span
            className="block h-full rounded-full bg-accent transition-[width] duration-700 ease-out"
            style={{
              width: `${Math.min(100, Math.round((ip / (ip + standing.ipToNextTier)) * 100))}%`,
            }}
          />
        </span>
      )}

      {/* §3.2 우하단 캡션 — 1줄 데이터형까지만(§5-1) */}
      <span className="self-end font-mono text-[10px] tabular-nums text-ink-mute">
        {today} snapshot · {SEASON_CONFIG.label}
      </span>
    </Link>
  );
}
